import * as React from 'react'
import { TriggerType } from '@pushly/models/lib/enums/journeys'
import { CatalogItemType } from '@pushly/models/lib/enums'
import { extractJourneyTrigger, isTriggerTypeOf } from '../../../../_utils/journeys'
import { generateDefaultSubscriberFcap } from '../../../../components/journey-builder/helpers/journey'
import {
    JOURNEY_DEFAULT_ABANDONMENT_METRIC,
    JOURNEY_DEFAULT_ABANDONMENT_SECONDS,
} from '../../../../components/journey-builder/configuration-editor/constants'
import { AbandonmentCatalogItemTypes } from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import type { ITriggerStepConfiguration } from '@pushly/models/lib/structs/journeys/steps/step-interfaces'
import type { Journey } from '../../types/journey-api-response'

export interface IUseTriggerHandlersArgs {
    journey: Journey
    catalogItemType: CatalogItemType
    defaultMaxAgeDays: number
    domainDefaultSegment?: { id: number } | null
    setJourney: (c: Journey) => void
    setFlags: (flags: Record<string, boolean>) => void
}

export function useTriggerHandlers({
    journey,
    catalogItemType,
    defaultMaxAgeDays,
    domainDefaultSegment,
    setJourney,
    setFlags,
}: IUseTriggerHandlersArgs) {
    const handleTriggerTypeChange = React.useCallback(
        (type: TriggerType) => {
            const updated = structuredClone(journey)
            const audience = updated.configuration.audience
            const trigger = extractJourneyTrigger(updated)

            const catalogItemTriggerTypes: TriggerType[] = [
                TriggerType.ABANDONED_CART,
                TriggerType.ABANDONED_VIEW,
                TriggerType.SAVED_ITEM,
                TriggerType.COMPLETED_ITEM,
            ]

            const currTriggerConfig = trigger?.configuration ?? {} as Partial<ITriggerStepConfiguration>
            const currTriggerIsCatalogItem = isTriggerTypeOf(
                currTriggerConfig.type!,
                catalogItemTriggerTypes,
            )

            // Start a typed next config
            const nextTriggerConfig: Partial<ITriggerStepConfiguration> = { ...currTriggerConfig, type }
            const nextTriggerIsCatalogItem = isTriggerTypeOf(type, catalogItemTriggerTypes)

            if (type === TriggerType.SUBSCRIBED) {
                // Reset audience to default/all
                audience.segment_ids = undefined
                audience.excluded_segment_ids = undefined

                // Minimal params for subscribed
                nextTriggerConfig.params = { event_type: type }
                if (trigger) {
                    trigger.configuration = nextTriggerConfig as ITriggerStepConfiguration
                }

                // Reset e‑commerce specific values and flags
                updated.configuration.subscriber_fcap = undefined
                updated.configuration.item_group_targeting = undefined
                updated.configuration.abandonment_seconds = undefined
                updated.configuration.abandonment_metric = undefined

                setFlags({ itemGroupsEnabled: false, quietHoursEnabled: false })
            } else if (nextTriggerIsCatalogItem) {
                // If moving from SUBSCRIBED to catalog: add initial audience
                if (!currTriggerIsCatalogItem) {
                    audience.segment_ids = !domainDefaultSegment ? [] : [domainDefaultSegment.id]
                }

                // Base params for catalog triggers
                nextTriggerConfig.params = { item_type: catalogItemType }

                if (type === TriggerType.ABANDONED_CART) {
                    // Add max item age for carts
                    nextTriggerConfig.params.max_item_age_days = defaultMaxAgeDays
                }

                if (trigger) {
                    trigger.configuration = nextTriggerConfig as ITriggerStepConfiguration
                }

                // Ensure FCAP exists for catalog triggers
                updated.configuration.subscriber_fcap = generateDefaultSubscriberFcap()

                // Ensure default values for item types that support abandonment
                if (AbandonmentCatalogItemTypes.includes(catalogItemType)) {
                    updated.configuration.abandonment_seconds = JOURNEY_DEFAULT_ABANDONMENT_SECONDS
                    updated.configuration.abandonment_metric = JOURNEY_DEFAULT_ABANDONMENT_METRIC
                }
            }

            // UI criteria flag reset
            setFlags({ criteriaEnabled: false })

            setJourney(updated)
        },
        [journey, catalogItemType, defaultMaxAgeDays, domainDefaultSegment, setJourney, setFlags],
    )

    return { handleTriggerTypeChange }
}
